import React, { useEffect, useMemo, useState } from 'react';
import {
  Appointment,
  useAdvanceAppointment,
  useAppointmentReminder,
  useAppointments,
} from '../services/api';
import useMockDataToast from '../hooks/useMockDataToast';
import { useAuth } from '../state/AuthContext';
import { useToasts } from '../state/ToastContext';

const STATUS_FILTERS = ['all', 'check-in', 'pre-op', 'follow-up', 'completed'] as const;
const TIME_FILTERS = [
  { id: 'all', label: 'All day' },
  { id: 'morning', label: 'Morning' },
  { id: 'afternoon', label: 'Afternoon' },
  { id: 'evening', label: 'Evening' },
] as const;

const providerSchedule = [
  { name: 'Dr. Avery', block: '08:00 - 14:00', focus: 'Internal medicine', load: '5/6 cases' },
  { name: 'Dr. Ramos', block: '10:00 - 18:00', focus: 'Surgery', load: '4 procedures' },
  { name: 'Dr. Patel', block: '12:00 - 20:00', focus: 'ICU lead', load: 'On call' },
];

const reminderQueue = [
  { label: 'Pre-op fasting reminders', status: '12 sent', owner: 'Automation' },
  { label: 'Lab results ready', status: '4 pending sign-off', owner: 'Lab' },
  { label: 'Client pickup ETA', status: '6 awaiting response', owner: 'Front desk' },
];

const STATUS_PIPELINE = ['Check-in', 'Pre-op', 'In progress', 'Recovery', 'Completed'];

const deriveNextStatus = (current?: string | null) => {
  if (!current) return STATUS_PIPELINE[0];
  const index = STATUS_PIPELINE.findIndex(
    (status) => status.toLowerCase() === current.toLowerCase(),
  );
  if (index === -1 || index === STATUS_PIPELINE.length - 1) {
    return STATUS_PIPELINE[STATUS_PIPELINE.length - 1];
  }
  return STATUS_PIPELINE[index + 1];
};

const Appointments: React.FC = () => {
  const {
    data: appointments = [],
    isLoading,
    isFallback,
    fallbackMessage,
  } = useAppointments();
  useMockDataToast(
    isFallback,
    'Appointment service unreachable. Showing cached schedule.',
    fallbackMessage,
  );
  const advanceAppointment = useAdvanceAppointment();
  const appointmentReminder = useAppointmentReminder();
  const { hasPermission } = useAuth();
  const { pushToast } = useToasts();

  const canAdvanceStatus = hasPermission('schedule:modify');
  const canSendReminder = hasPermission('schedule:view') || hasPermission('schedule:modify');

  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('all');
  const [timeFilter, setTimeFilter] = useState<(typeof TIME_FILTERS)[number]['id']>('all');
  const [search, setSearch] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  const filteredAppointments = useMemo(() => {
    return appointments
      .filter((appt) => {
        const statusMatch =
          statusFilter === 'all' ||
          (appt.status ?? 'scheduled').toLowerCase() === statusFilter.toLowerCase();
        if (!statusMatch) return false;

        if (timeFilter === 'all') return true;
        const hour = new Date(appt.date).getHours();
        if (timeFilter === 'morning') return hour < 12;
        if (timeFilter === 'afternoon') return hour >= 12 && hour < 17;
        if (timeFilter === 'evening') return hour >= 17;
        return true;
      })
      .filter((appt) => {
        if (!search.trim()) return true;
        const needle = search.toLowerCase();
        return (
          appt.petName.toLowerCase().includes(needle) ||
          (appt.owner ?? '').toLowerCase().includes(needle) ||
          (appt.vetName ?? '').toLowerCase().includes(needle)
        );
      });
  }, [appointments, statusFilter, timeFilter, search]);

  useEffect(() => {
    if (!selectedAppointment && filteredAppointments.length > 0) {
      setSelectedAppointment(filteredAppointments[0]);
    } else if (
      selectedAppointment &&
      !filteredAppointments.find((appt) => appt.id === selectedAppointment.id)
    ) {
      setSelectedAppointment(filteredAppointments[0] ?? null);
    }
  }, [filteredAppointments, selectedAppointment]);

  const handleAdvanceStatus = async () => {
    if (!selectedAppointment || !canAdvanceStatus) return;
    const nextStatus = deriveNextStatus(selectedAppointment.status);
    try {
      const result = await advanceAppointment.mutateAsync({
        appointmentId: selectedAppointment.id,
        status: nextStatus,
      });
      setSelectedAppointment((prev) =>
        prev && prev.id === selectedAppointment.id ? { ...prev, status: result.status ?? prev.status } : prev,
      );
      pushToast({
        tone: 'success',
        message: `Moved ${selectedAppointment.petName} to ${result.status ?? nextStatus}`,
      });
    } catch (error) {
      pushToast({
        tone: 'error',
        message: 'Unable to advance status',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleSendReminder = async () => {
    if (!selectedAppointment || !canSendReminder) return;
    try {
      await appointmentReminder.mutateAsync({
        appointmentId: selectedAppointment.id,
        channel: 'SMS',
      });
      pushToast({
        tone: 'success',
        message: `Reminder sent to ${selectedAppointment.owner ?? 'client'}`,
      });
    } catch (error) {
      pushToast({
        tone: 'error',
        message: 'Unable to send reminder',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const statusSummary = useMemo(() => {
    const map = new Map<string, number>();
    appointments.forEach((appt) => {
      const status = (appt.status ?? 'Scheduled').toLowerCase();
      map.set(status, (map.get(status) ?? 0) + 1);
    });
    return Array.from(map.entries()).map(([status, count]) => ({
      status: status.replace(/\b\w/g, (char) => char.toUpperCase()),
      count,
    }));
  }, [appointments]);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="section-title">Clinical scheduling</p>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">Appointments</h1>
          <div className="flex gap-3">
            <button className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-brand-blue hover:text-brand-blue">
              Upload referrals
            </button>
            <button className="rounded-xl bg-brand-blue px-4 py-2 text-sm font-semibold text-white shadow-soft">
              + Schedule appointment
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setStatusFilter(filter)}
                className={`rounded-2xl px-3 py-1 text-xs font-semibold capitalize ${
                  statusFilter === filter
                    ? 'bg-brand-blue text-white'
                    : 'bg-white text-slate-500 shadow-sm'
                }`}
              >
                {filter === 'all' ? 'All statuses' : filter}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {TIME_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setTimeFilter(filter.id)}
                className={`rounded-2xl px-3 py-1 text-xs font-semibold ${
                  timeFilter === filter.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-500 shadow-sm'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            placeholder="Search patient, owner, or provider"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="min-w-[220px] flex-1 rounded-2xl border border-slate-200 bg-white px-3 py-1 text-xs focus:border-brand-blue focus:ring-brand-blue"
          />
        </div>
      </div>

      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-auto divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-6 py-3">Time</th>
                <th className="px-6 py-3">Patient</th>
                <th className="px-6 py-3">Provider</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Room</th>
                <th className="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    Loading schedule...
                  </td>
                </tr>
              ) : filteredAppointments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    No appointments match the selected filters.
                  </td>
                </tr>
              ) : (
                filteredAppointments.map((appt) => (
                  <tr
                    key={appt.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setSelectedAppointment(appt)}
                  >
                    <td className="px-6 py-4 text-slate-500">
                      {new Date(appt.date).toLocaleString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{appt.petName}</p>
                      <p className="text-xs text-slate-500">{appt.owner ?? 'Owner TBD'}</p>
                    </td>
                    <td className="px-6 py-4">{appt.vetName}</td>
                    <td className="px-6 py-4">
                      <span className="pill bg-indigo-50 text-brand-indigo">
                        {appt.type ?? 'General consult'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{appt.room ?? 'Treatment A'}</td>
                    <td className="px-6 py-4">
                      <span className="pill bg-slate-100 text-slate-700">
                        {appt.status ?? 'Scheduled'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="glass-panel space-y-4 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="section-title mb-1">Staffing</p>
              <h3 className="text-lg font-semibold text-slate-900">Provider schedule</h3>
            </div>
            <button className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-brand-blue hover:text-brand-blue">
              Export to Slack
            </button>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {providerSchedule.map((provider) => (
              <div
                key={provider.name}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm"
              >
                <p className="font-semibold text-slate-900">{provider.name}</p>
                <p className="text-xs text-slate-500">{provider.block}</p>
                <p className="mt-2 text-xs text-slate-500">{provider.focus}</p>
                <p className="mt-2 text-xs font-semibold text-brand-blue">{provider.load}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-panel space-y-3 p-6">
          <div>
            <p className="section-title mb-1">Queue health</p>
            <h3 className="text-lg font-semibold text-slate-900">Automation status</h3>
          </div>
          <ul className="space-y-2 text-sm">
            {reminderQueue.map((item) => (
              <li
                key={item.label}
                className="rounded-2xl border border-slate-100 bg-white px-4 py-3"
              >
                <p className="font-semibold text-slate-900">{item.label}</p>
                <p className="text-xs text-slate-500">{item.owner}</p>
                <p className="text-xs font-semibold text-brand-blue">{item.status}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="glass-panel space-y-3 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="section-title mb-1">Status mix</p>
              <h3 className="text-lg font-semibold text-slate-900">Live triage snapshot</h3>
            </div>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">
              {statusSummary.reduce((total, item) => total + item.count, 0)} scheduled
            </span>
          </div>
          <ul className="space-y-2 text-sm">
            {statusSummary.map((summary) => (
              <li
                key={summary.status}
                className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-2"
              >
                <span>{summary.status}</span>
                <span className="font-semibold text-slate-900">{summary.count}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="glass-panel space-y-4 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="section-title mb-1">Appointment detail</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedAppointment ? selectedAppointment.petName : 'Select an appointment'}
              </h3>
              {selectedAppointment && (
                <p className="text-xs text-slate-500">
                  {selectedAppointment.owner ?? 'Owner TBD'} 繚{' '}
                  {new Date(selectedAppointment.date).toLocaleString()}
                </p>
              )}
            </div>
            {selectedAppointment && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSendReminder}
                  disabled={!canSendReminder || appointmentReminder.isPending}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-brand-blue hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {appointmentReminder.isPending ? 'Sending…' : 'Send reminder'}
                </button>
                <button
                  type="button"
                  onClick={handleAdvanceStatus}
                  disabled={!canAdvanceStatus || advanceAppointment.isPending}
                  className="rounded-xl bg-brand-blue px-3 py-2 text-xs font-semibold text-white shadow-soft transition disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {advanceAppointment.isPending ? 'Moving…' : 'Advance status'}
                </button>
              </div>
            )}
          </div>

          {selectedAppointment ? (
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Procedure type
                </p>
                <p className="mt-2 text-slate-900">
                  {selectedAppointment.type ?? 'General consult'}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Room {selectedAppointment.room ?? 'TBD'}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Provider
                </p>
                <p className="mt-2 text-slate-900">{selectedAppointment.vetName}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Status: {selectedAppointment.status ?? 'Scheduled'}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Actions
                </p>
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  <li>Confirm fasting + medication checklist</li>
                  <li>Attach latest labs to chart</li>
                  <li>Notify owner of pickup ETA</li>
                </ul>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Select an appointment to preview details.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Appointments;
